import { useState } from "react";

interface SubcategoryCircleProps {
  name: string;
  emoji: string;
  staticImage?: string;
  dbImage?: string;
  /** When true, shows a shimmer placeholder instead of emoji while DB images load */
  dbImageLoading?: boolean;
  gradientColor?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses = {
  sm: "w-7 h-7",
  md: "w-8 h-8",
  lg: "w-16 h-16 sm:w-20 sm:h-20",
};

export default function SubcategoryCircle({
  name,
  emoji,
  staticImage,
  dbImage,
  dbImageLoading = false,
  gradientColor = "from-primary/20 to-primary/40",
  size = "md",
  className = "",
}: SubcategoryCircleProps) {
  const [staticFailed, setStaticFailed] = useState(false);
  const [dbFailed, setDbFailed] = useState(false);

  const sizeClass = sizeClasses[size];
  const hasStaticImage = staticImage && !staticImage.includes("placeholder") && !staticFailed;
  const hasDbImage = dbImage && !dbFailed;
  const showImage = hasStaticImage || hasDbImage;
  const imageSrc = hasStaticImage ? staticImage : dbImage;

  // If no static image and DB images are still loading, show shimmer
  const showShimmer = !hasStaticImage && !hasDbImage && dbImageLoading;

  const emojiSize = size === "lg" ? "text-2xl sm:text-3xl" : size === "md" ? "text-base" : "text-sm";

  return (
    <div
      className={`${sizeClass} rounded-full overflow-hidden flex items-center justify-center shrink-0 ${
        showImage ? "bg-muted" : showShimmer ? "bg-muted animate-pulse" : `bg-gradient-to-br ${gradientColor}`
      } ${className}`}
    >
      {showImage ? (
        <img
          src={imageSrc}
          alt={name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => {
            if (hasStaticImage) setStaticFailed(true);
            else setDbFailed(true);
          }}
        />
      ) : showShimmer ? null : (
        <span className={`${emojiSize} drop-shadow-md`}>{emoji}</span>
      )}
    </div>
  );
}
